import { z } from 'zod';

import { tolerantArray } from '@/lib/tolerant-array';

export const ANALYSIS_ENDPOINT = 'api/v1/agro1/laboratorio/analises_laboratorios';

export const UNKNOWN_ANALYSIS_STATUS = 'unknown';

const PENDING_ANALYSIS_STATUS = 'N';

export const analysisStatusSchema = z
  .preprocess(
    (status) => status ?? PENDING_ANALYSIS_STATUS,
    z.enum([PENDING_ANALYSIS_STATUS, 'I', 'C', UNKNOWN_ANALYSIS_STATUS]),
  )
  .catch(UNKNOWN_ANALYSIS_STATUS);

export type AnalysisApiStatus = z.infer<typeof analysisStatusSchema>;

export const ANALYSIS_STATUS_LABEL_VALUES = [
  'Pendente',
  'Iniciado',
  'Realizado',
  'Desconhecido',
] as const;

export type AnalysisStatusLabel = (typeof ANALYSIS_STATUS_LABEL_VALUES)[number];

export const analysisStatusLabelSchema = z.enum(ANALYSIS_STATUS_LABEL_VALUES);

export const ANALYSIS_STATUS_LABELS = {
  N: 'Pendente',
  I: 'Iniciado',
  C: 'Realizado',
  [UNKNOWN_ANALYSIS_STATUS]: 'Desconhecido',
} as const satisfies Record<AnalysisApiStatus, AnalysisStatusLabel>;

export const ANALYSIS_STATUS_FILTERS = ['Pendente', 'Iniciado', 'Realizado'] as const;

export type AnalysisStatusFilter = (typeof ANALYSIS_STATUS_FILTERS)[number];

export const ANALYSIS_STATUS_FILTER_VALUES = {
  Pendente: 'pendente',
  Iniciado: 'iniciado',
  Realizado: 'realizado',
} as const satisfies Record<AnalysisStatusFilter, string>;

const analysisStatusFilterSchema = z.enum(ANALYSIS_STATUS_FILTERS);

export function parseStatusFilters(value: string | undefined): AnalysisStatusFilter[] {
  if (!value) return [];

  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(
      (entry): entry is AnalysisStatusFilter => analysisStatusFilterSchema.safeParse(entry).success,
    );
}

export function serializeStatusFilters(values: AnalysisStatusFilter[]): string | undefined {
  return values.length > 0 ? values.join(',') : undefined;
}

const numericSchema = z.union([z.number(), z.string()]);

const addressSchema = z
  .object({
    logradouro: z.string().nullish(),
    numero: z.string().nullish(),
    complemento: z.string().nullish(),
    bairro: z.string().nullish(),
    cidade: z.string().nullish(),
    estado: z.string().nullish(),
    cep: z.string().nullish(),
  })
  .partial();

const partySchema = z.object({
  id: z.string(),
  nome: z.string(),
  nome_fantasia: z.string().nullish(),
  documento: z.string().nullish(),
  inscricao: z.string().nullish(),
  telefone: z.string().nullish(),
  email: z.string().nullish(),
  endereco: addressSchema.nullish(),
});

export const collectionSchema = z.object({
  id: z.string(),
  numero_remessa: z.string().nullish(),
  data_entrada: z.string().nullish(),
  data_amostra: z.string().nullish(),
  data_prazo_entrega: z.string().nullish(),
  safra: z.string().nullish(),
  nome_cientifico: z.string().nullish(),
  atividade: z.string().nullish(),
  id_atividade: z.string().nullish(),
  tipo_analise: z.string().nullish(),
});

export const sampleSchema = z.object({
  id: z.string().nullish(),
  id_amostra: z.number().nullish(),
  numero_amostra: z.string().nullish(),
  cultivar: z.string().nullish(),
  cultivar_longo: z.string().nullish(),
  nome_cientifico: z.string().nullish(),
  categoria: z.string().nullish(),
  especie: z.string().nullish(),
  numero_lote: z.string().nullish(),
  observacao: z.string().nullish(),
  umidade: numericSchema.nullish(),
});

export const determinationSchema = z.object({
  id: z.string(),
  ordem: z.number().nullish(),
  descricao: z.string().nullish(),
  descricao_reduzida: z.string().nullish(),
  abreviatura: z.string().nullish(),
  id_tabela_tolerancia: z.string().nullish(),
});

export const repetitionProgressSchema = z.object({
  repeticao: z.string(),
  progresso: z.string().nullish(),
});

export const damageSchema = z.object({
  id: z.string(),
  intenso: z.boolean(),
});

export const seedClassificationSchema = z.object({
  id: numericSchema.nullish(),
  contagem: numericSchema,
  grau: z.string().nullish(),
  danos: z.array(damageSchema).default([]),
});

type SeedClassificationList = z.infer<typeof seedClassificationSchema>[];

function fallbackToMisspelledField(
  misspelledClassificaoesSementes: SeedClassificationList | null | undefined,
): SeedClassificationList {
  return misspelledClassificaoesSementes ?? [];
}

export const repetitionResultSchema = z
  .object({
    repeticao: z.string(),
    classificacoes_sementes: z.array(seedClassificationSchema).nullish(),
    classificaoes_sementes: z.array(seedClassificationSchema).nullish(),
  })
  .transform(({ repeticao, classificacoes_sementes, classificaoes_sementes }) => ({
    repetition: repeticao,
    classifications: classificacoes_sementes ?? fallbackToMisspelledField(classificaoes_sementes),
  }));

export const sandBedResultsSchema = z.object({
  amostras: z.array(numericSchema).default([]),
  media: numericSchema.nullish(),
});

const referenceSchema = z.object({
  id: z.string(),
  descricao: z.string().nullish(),
});

function emptyStringToNull(value: unknown): unknown {
  return value === '' ? null : value;
}

function optionalObjectSchema<TSchema extends z.ZodTypeAny>(schema: TSchema) {
  return z.preprocess(emptyStringToNull, schema.nullish());
}

const optionalReferenceSchema = optionalObjectSchema(referenceSchema);

type RawRecord = Record<string, unknown>;

function isRawRecord(value: unknown): value is RawRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function namedReference(value: unknown): unknown {
  if (typeof value !== 'string') return value;

  return value ? { id: value, descricao: value } : null;
}

function normalizeStep(value: unknown): unknown {
  if (!isRawRecord(value) || !('id_analista' in value)) return value;

  const { id_analista, horario, equipamento, termohigrometro, tratamento, substrato, ...rest } =
    value;

  return {
    ...rest,
    ...(tratamento !== undefined && { id: tratamento || null, descricao: tratamento }),
    ...(substrato !== undefined && { substrato: namedReference(substrato) }),
    hora_entrada: horario,
    equipamento: namedReference(equipamento),
    termohigrometro: namedReference(termohigrometro),
    analista: id_analista ? { id: id_analista } : null,
  };
}

function optionalStepSchema<TSchema extends z.ZodTypeAny>(schema: TSchema) {
  return z.preprocess((value) => normalizeStep(emptyStringToNull(value)), schema.nullish());
}

const labStepSchema = z.object({
  data_entrada: z.string().nullish(),
  hora_entrada: z.string().nullish(),
  data_saida: z.string().nullish(),
  hora_saida: z.string().nullish(),
  equipamento: optionalReferenceSchema,
  termohigrometro: optionalReferenceSchema,
  analista: optionalReferenceSchema,
  observacao: z.string().nullish(),
});

export const treatmentSchema = labStepSchema.extend({
  id: z.string().nullish(),
  descricao: z.string().nullish(),
});

export const installationStepSchema = labStepSchema.extend({
  substrato: optionalReferenceSchema,
  solucao: z.string().nullish(),
});

export type LabStep = z.infer<typeof labStepSchema>;

const germinationBlockSchema = z.object({
  normais: numericSchema.nullish(),
  anormais: numericSchema.nullish(),
  duras: numericSchema.nullish(),
  dormentes: numericSchema.nullish(),
  mortas: numericSchema.nullish(),
  intermediarias: numericSchema.nullish(),
  fracas: numericSchema.nullish(),
});

const germinationRepetitionSchema = z.object({
  repeticao: z.string(),
  blocos: z.array(germinationBlockSchema).default([]),
});

export const germinationReadingSchema = z.object({
  numero: numericSchema,
  data_leitura: z.string().nullish(),
  repeticoes: z.array(germinationRepetitionSchema).default([]),
  possui_anormalidade: z.boolean().nullish(),
  anormalidade: optionalReferenceSchema,
});

export type GerminationReadingPayload = z.infer<typeof germinationReadingSchema>;

export const analysisImageSchema = z.object({
  erp_id: z.string(),
  name: z.string(),
  size: z.number().nullish(),
  url: z.string().nullish(),
  urls: z
    .object({
      thumbnail: z.string(),
      fullsize: z.string(),
    })
    .optional(),
});

const labAnalysisObject = z.object({
  id: z.string(),
  coleta: collectionSchema,
  amostra: sampleSchema,
  determinacao: determinationSchema,
  requerente: partySchema,
  analista: partySchema.partial({ nome: true }).nullish(),
  produtor: partySchema.partial({ nome: true }).nullish(),
  status: analysisStatusSchema,
  reteste: z.boolean().nullish(),
  observacao: z.string().nullish(),
  data_instalacao: z.string().nullish(),
  tratamento: optionalStepSchema(treatmentSchema),
  instalacao: optionalStepSchema(installationStepSchema),
  total_sementes: numericSchema.nullish(),
  data_analise: z.string().nullish(),
  data_termino_analise: z.string().nullish(),
  temperatura_minima: numericSchema.nullish(),
  temperatura_maxima: numericSchema.nullish(),
  temperatura_inicial: numericSchema.nullish(),
  temperatura_final: numericSchema.nullish(),
  numero_leituras: numericSchema.nullish(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish(),
  numero_repeticoes: numericSchema.nullish(),
  sementes_repeticao: numericSchema.nullish(),
  solucao: numericSchema.nullish(),
  gerar_bas_rir: z.boolean().nullish(),
  possui_bas: z.boolean().nullish(),
  possui_rir: z.boolean().nullish(),
  repeticao_rejeitada: z.string().nullish(),
  progresso_repeticoes: tolerantArray(
    repetitionProgressSchema,
    `${ANALYSIS_ENDPOINT}.progresso_repeticoes`,
  ).nullish(),
  resultados: z.union([z.array(repetitionResultSchema), sandBedResultsSchema]).nullish(),
  imagens: tolerantArray(analysisImageSchema, `${ANALYSIS_ENDPOINT}.imagens`).nullish(),
  concluido_api: z.boolean().nullish(),
  integration_status: numericSchema.nullish(),
  integration_errors: z.array(z.unknown()).nullish(),
  leituras: tolerantArray(germinationReadingSchema, `${ANALYSIS_ENDPOINT}.leituras`).nullish(),
  repeticoes_anuladas: z.array(z.string()).nullish(),
  tabela_tolerancia_verificada: z.boolean().nullish(),
  anormalidade: optionalReferenceSchema,
  id_analise_origem: z.string().nullish(),
});

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

const FLAT_STEP_KEYS = [
  'equipamento_tratamento',
  'termohigrometro_tratamento',
  'equipamento_instalacao',
  'termohigrometro_instalacao',
];

function flatTreatment(raw: RawRecord): unknown {
  const { tratamento, equipamento_tratamento, termohigrometro_tratamento } = raw;
  const hasFlat =
    hasText(tratamento) || hasText(equipamento_tratamento) || hasText(termohigrometro_tratamento);

  if (!hasFlat) return isRawRecord(tratamento) ? tratamento : (tratamento ?? null);

  return {
    ...(isRawRecord(tratamento) ? tratamento : {}),
    id: hasText(tratamento) ? tratamento : null,
    descricao: hasText(tratamento) ? tratamento : null,
    equipamento: namedReference(equipamento_tratamento),
    termohigrometro: namedReference(termohigrometro_tratamento),
  };
}

function flatInstallation(raw: RawRecord, started: boolean): unknown {
  const { instalacao, substrato, equipamento_instalacao, termohigrometro_instalacao } = raw;
  const hasFlat =
    hasText(substrato) || hasText(equipamento_instalacao) || hasText(termohigrometro_instalacao);

  if (!hasFlat) return instalacao ?? null;

  return {
    substrato: namedReference(substrato),
    solucao: hasText(raw.solucao) ? raw.solucao : null,
    equipamento: namedReference(equipamento_instalacao),
    termohigrometro: namedReference(termohigrometro_instalacao),
    data_entrada: hasText(raw.data_instalacao) ? raw.data_instalacao : null,
    data_saida: !started && hasText(raw.data_analise) ? raw.data_analise : null,
  };
}

function flattenSteps(value: unknown): unknown {
  if (!isRawRecord(value)) return value;

  const isFlat =
    hasText(value.tratamento) ||
    hasText(value.substrato) ||
    FLAT_STEP_KEYS.some((key) => hasText(value[key]));

  if (!isFlat) return value;

  const started = isRawRecord(value.analista) && hasText(value.analista.id);
  const { data_analise, ...rest } = value;

  return {
    ...rest,
    ...(started && { data_analise }),
    tratamento: flatTreatment(value),
    instalacao: flatInstallation(value, started),
  };
}

const labAnalysisSchema = z.preprocess(flattenSteps, labAnalysisObject);

export type LabAnalysis = z.infer<typeof labAnalysisObject>;

export const installationSampleSchema = z.preprocess(
  flattenSteps,
  labAnalysisObject.extend({
    requerente: partySchema.catch({ id: '', nome: '' }),
    deleted: z.boolean().nullish(),
  }),
);

export const analysisListResponseSchema = z.object({
  total: z.number(),
  page: z.number(),
  per_page: z.number(),
  total_pages: z.number(),
  data: tolerantArray(labAnalysisSchema, ANALYSIS_ENDPOINT),
});

export const analysisByIdResponseSchema = z.object({
  data: z.array(labAnalysisSchema),
});

export type AnalysisListResponse = z.infer<typeof analysisListResponseSchema>;

export type AnalysisListMeta = {
  currentPage: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
};

export type AnalysisListParams = {
  produtorId: string;
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
  installationStatus?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
  dataInicio?: string;
  dataFim?: string;
  dataAnaliseInicio?: string;
  dataAnaliseFim?: string;
  determinationIds?: string[];
};
